<template>
  <cashflow-loader v-if="showContentLoader" />
  <div class="page">
    <riseup-header />
    <div
      class="back-arrow-container"
      @click="handleBackClick"
    >
      <img
        src="../../assets/arrow_back.svg"
        alt="Back Arrow"
        class="back-arrow"
      >
    </div>
    <div class="content">
      <h1>Let’s connect your account</h1>
      <div class="text">
        To get the best results, connect the account you use most for spending.
      </div>
      <img
        class="hands"
        src="../../assets/hands.svg"
      >
    </div>
    <div class="footer">
      <div class="flex-container">
        <div class="flex-box">
          <div class="icon">
            <img
              src="../../assets/shield.svg"
              alt="Shield Icon"
            >
          </div>
          <div class="text">
            <span>We don’t keep your data, we only connect once to create your insights</span>
          </div>
        </div>
      </div>
      <button
        class="blue-button"
        @click="handleClick"
      >
        Get started!
      </button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import RiseupHeader from '../Common/RiseupHeader.vue';
import CashflowLoader from './CashflowLoader.vue';
import Segment from '../../Segment';
import useContentLoader from '../../composables/useContentLoader';
import { authenticateTrueLayerCode, getTrueLayerUrl } from '../../api/trueLayer';
import config from '../../config';
import { useSessionStore } from '../../stores/session';

const sessionStore = useSessionStore();

Segment.identifyCustomer(sessionStore.customerId, sessionStore.memberId);
Segment.trackUserGot('ConnectOtsAccountPageEntered');
const { toggleLoader, showContentLoader } = useContentLoader();

const showConnectDetails = ref(false);

const handleClick = async () => {
  showConnectDetails.value = false;
  Segment.trackUserGot('ConnectOtsAccountsConnectClicked');
  window.location.href = await getTrueLayerUrl();
};

const handleBackClick = () => {
  Segment.trackUserGot('ConnectOtsAccountsBackClicked');
  window.location.href = config.get().otsLoginUrl;
};

const route = useRoute();

onMounted(async () => {
  toggleLoader(false);
  const queryParam = route.query || null;

  if (queryParam.code) {
    toggleLoader(true);
    const response = await authenticateTrueLayerCode(queryParam.code);
    if (response) {
      await sleep(5000);
      window.location.href = '/ots/success';
    }
  }
});

// eslint-disable-next-line no-promise-executor-return
const sleep = ms => new Promise(resolve => setTimeout(() => resolve(), ms));
</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.page {
  background-color: $main_background_color;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  overflow-y: auto;
  scroll-behavior: smooth;
  padding: 25px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.content h1 {
  color: #202125;
  font-size: 28px;
  font-family: GrenetteSemiBold;
  font-weight: 600;
  line-height: 39px;
  margin-bottom: 10px;
}

.content .text {
  color: #202125;
  font-size: 18px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 28px;
  margin-bottom: 20px;
}

.hands {
  width: 200px;
  align-self: center;
  margin-top: 22px;
}

.flex-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 90%;
  padding-top: 20px;
}

.flex-box {
  padding: 10px;
  background: rgba(0, 192, 112, 0.1);
  border-radius: 18px;
  display: flex;
  align-items: center;
  width: 100%;
}

.icon {
  margin-right: 20px;
}

.icon img {
  width: 30px;
  height: 30px;
}

.back-arrow-container {
  padding: 21px;
  cursor: pointer;
}

.back-arrow {
  width: 24px;
  height: 24px;
}

.footer {
  width: 100%;
  text-align: center;
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.flex-box .text {
  // We don’t keep your data, we only connect once to create your insights.
  color: #205441;
  font-size: 16px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 24px;
  word-wrap: break-word;
  text-align: left;
  margin: 0 auto;
}

.blue-button {
  background-color: #5D7AFD;
  border-radius: 10px;
  color: white;
  padding: 10px 20px;
  border: none;
  margin: 20px 0;
  width: 90%;
  cursor: pointer;
  font-size: 18px;
  font-family: CircularStdBook;
  font-weight: 700;
}
</style>
